import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SwatchDataValue } from '../types/data';
import type { ProductRoot } from '../types/product';
import { useGetInstockFabricOptions } from './use-get-instock-fabric-options';

type FabricOptionProps = {
  item: {
    uid: string;
    label: string;
    values: SwatchDataValue[];
  };
  createFabricButton?: React.ReactNode;
  handleClick: any;
  clickIndex: number;
  optionSpecs: any;
  product: ProductRoot;
};

export const FabricOptions = ({
  item,
  createFabricButton,
  handleClick,
  clickIndex,
  optionSpecs,
  product,
}: FabricOptionProps) => {
  const form = useFormContext();
  const { watch, control } = form;

  const allFormData = watch();

  const { values } = useGetInstockFabricOptions({
    product,
    oldValues: item?.values,
  });

  const [showFabricDialog, setShowFabricDialog] = useState(false);

  return (
    <>
      <FormField
        key={item.uid}
        control={form.control}
        name={item.label}
        render={({ field }) => (
          <FormItem
            suppressHydrationWarning
            className='space-y-2.5 bg-white  px-2.5  py-[15px] lg_p-0 lg_px-0'
          >
            <div className='flex items-center justify-between lg_justify-start'>
              <FormLabel className='option_title__x4i8y text-colorDefault flex items-center gap-x-[5px] '>
                <span className='text-base font-medium uppercase leading-5 lg_font-normal'>{item.label}: </span>
                <span className='text-[15px] leading-5'>{allFormData[item.label]?.label.replace('-default', '')}</span>
              </FormLabel>
              <button
                type='button'
                className='text-[15px] leading-5 underline lg_hidden'
                onClick={() => {
                  setShowFabricDialog(true);
                }}
              >
                Fabric Details
              </button>
            </div>
            <FormMessage id={item.label} />
            <FormControl suppressHydrationWarning>
              <RadioGroup
                suppressHydrationWarning
                onValueChange={value => {
                  const selectedOption = values.find(v => v.value_index.toString() === value);

                  if (selectedOption) {
                    field.onChange({
                      value_index: value,
                      label: selectedOption?.label,
                      price: selectedOption?.price,
                      option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                    });
                    form.resetField('Color');
                    form.resetField('Size');
                  }

                  const couldSelect = optionSpecs.includes(value);

                  // 布料永远加入顶点
                  handleClick({
                    bool: true,
                    text: value,
                    index: clickIndex,
                    isFabric: true,
                  });

                  // fieldSize.onChange(undefined);

                  // fieldColor.onChange(undefined);
                }}
                value={field.value?.value_index}
                className='flex flex-wrap gap-2.5'
              >
                {values.map(option => (
                  <FabricRadioItem
                    key={option.uid}
                    item={option}
                    optionSpecs={optionSpecs}
                  />
                ))}
                {createFabricButton}
              </RadioGroup>
            </FormControl>

            <div className='hidden lg_block'>
              <span className='text-sm leading-5'>
                This dress is available in different fabric options, view the difference
              </span>
              &nbsp;
              <button
                type='button'
                className='text-sm underline'
                onClick={() => {
                  setShowFabricDialog(true);
                }}
              >
                here
              </button>
            </div>
          </FormItem>
        )}
      />
      <FabricDialog
        open={showFabricDialog}
        onOpenChange={setShowFabricDialog}
      />
    </>
  );
};

const FabricRadioItem = ({ item, optionSpecs }: { item: SwatchDataValue; optionSpecs: any }) => {
  const inStock = optionSpecs.includes(item.value_index.toString());

  // const isOutOfStock = outOfStockVariants.flat().includes(item.value_index);
  const isOutOfStock = !inStock;

  return (
    <FormItem
      suppressHydrationWarning
      className='flex max-w-[150px] flex-1 items-center space-y-0'
    >
      <FormControl suppressHydrationWarning>
        <RadioGroupItem
          value={item.value_index.toString()}
          className='peer sr-only'
          suppressHydrationWarning
        />
      </FormControl>
      <FormLabel
        suppressHydrationWarning
        className={cn(
          'tile_root__gozBD flex min-h-[36px] w-full flex-1 cursor-pointer items-center justify-center  rounded-[3px] border-1 border-solid border-80583F bg-f8f3ef px-1 text-80583F peer-data-[state=checked]_bg-primary peer-data-[state=checked]_text-white  [&:has([data-state=checked])]_text-white ',
          // isOutOfStock && "opacity-50"
        )}
      >
        {item.label.replace('-default', '')}
      </FormLabel>
    </FormItem>
  );
};

const FabricDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (nextOpen: boolean) => void }) => {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className='h-[calc(100vh-40px)] w-[calc(100vw-40px)] p-0 lg_h-[590px] lg_w-[1200px] lg_max-w-[1200px] '>
        <DialogTitle className='hidden' />
        <div className='h-full overflow-y-auto px-[10px] py-[32px] lg_p-[78px]'>
          <div className='flex flex-col gap-9'>
            <div className='flex flex-col justify-between gap-[18px] lg_flex-row lg_gap-9'>
              <Image
                src={`${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric.webp`}
                alt='Crystal Satin'
                width={400}
                height={400}
                className='w-full lg_w-[400px]'
              />

              <div className='flex-1'>
                <span className='inline-block w-full border-b-1 border-solid border-[#E7DED5] text-center font-Shango text-[24px] leading-[52px] text-primary lg_w-fit lg_text-left lg_text-[32px]'>
                  Fabric: Crystal Satin
                </span>
                <h4 className='mt-[30px] text-center font-Shango text-[22px] text-primary lg_text-left'>Features</h4>
                <div className='mt-[22px] flex gap-[6px] lg_gap-4'>
                  {[
                    {
                      title: 'Strong Luster',
                      image: `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-item-1.webp`,
                      width: 69,
                      height: 79,
                    },
                    {
                      title: 'Durable',
                      image: `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-item-2.webp`,
                      width: 116,
                      height: 114,
                    },
                    {
                      title: 'Beautiful Drape',
                      image: `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-item-3.webp`,
                      width: 81,
                      height: 70,
                    },
                  ].map((i, index) => (
                    <div
                      key={index}
                      className='flex h-[131px] w-1/3 flex-col items-center justify-center bg-[#f8f3ef] lg_h-[212px]'
                    >
                      <div className='flex h-[45px] w-[45px] items-center justify-center lg_h-[114px] lg_w-[114px]'>
                        <Image
                          src={i.image}
                          alt={i.title}
                          width={i.width}
                          height={i.height}
                          className='h-full w-full lg_h-fit lg_w-fit'
                        />
                      </div>
                      <h5 className='mt-[18px] text-center font-Shango text-[16px] text-primary lg_mt-0 lg_text-[22px]'>
                        {i.title}
                      </h5>
                    </div>
                  ))}
                </div>

                <div className='mt-[45px] flex justify-center gap-4'>
                  <Link
                    href='/fabrics/crystal-satin-vs-satin-charmeuse-comparison'
                    className='flex h-10 w-1/2 flex-1 items-center justify-center border-1 border-solid border-80583F text-80583F lg_w-auto lg_min-w-[165px] lg_flex-none lg_hover_bg-80583F lg_hover_text-white'
                  >
                    LEARN MORE
                  </Link>
                  <Link
                    href='/aw-crystal-satin-swatches-for-bridal-dresses-sk24110cscp.html'
                    className='flex h-10 w-1/2 flex-1 items-center justify-center border-1 border-solid border-80583F  bg-80583F uppercase text-white lg_w-auto lg_min-w-[165px] lg_flex-none lg_hover_bg-white lg_hover_text-80583F'
                  >
                    Shop Swatches
                  </Link>
                </div>
              </div>
            </div>

            <div className='flex flex-col-reverse justify-between gap-9 lg_flex-row'>
              <div className='flex-1'>
                <span className='inline-block w-full border-b-1 border-solid border-[#E7DED5] text-center font-Shango text-[24px] leading-[52px] text-primary lg_w-fit lg_text-left lg_text-[32px]'>
                  Fabric: Satin Charmeuse
                </span>
                <h4 className='mt-[30px] text-center font-Shango text-[22px] text-primary lg_text-left'>Features</h4>
                <div className='mt-[22px] flex gap-[6px] lg_gap-4'>
                  {[
                    {
                      title: 'Relatively Subtle Sheen',
                      image: `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-item-4.webp`,
                      width: 69,
                      height: 79,
                    },
                    {
                      title: 'Silky',
                      image: `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-item-5.webp`,
                      width: 116,
                      height: 114,
                    },
                    {
                      title: 'Soft',
                      image: `${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-item-6.webp`,
                      width: 81,
                      height: 70,
                    },
                  ].map((i, index) => (
                    <div
                      key={index}
                      className='flex h-[131px] w-1/3 flex-col items-center justify-center bg-[#f8f3ef] lg_h-[212px]'
                    >
                      <div className='flex h-[45px] w-[45px] items-center justify-center lg_h-[114px] lg_w-[114px]'>
                        <Image
                          src={i.image}
                          alt={i.title}
                          width={i.width}
                          height={i.height}
                          className='h-full w-full lg_h-fit lg_w-fit'
                        />
                      </div>
                      <h5 className='mt-[18px] text-center font-Shango text-[16px] text-primary lg_mt-0 lg_text-[22px]'>
                        {i.title}
                      </h5>
                    </div>
                  ))}
                </div>

                <div className='mt-[45px] flex justify-center gap-4'>
                  <Link
                    href='/fabrics/crystal-satin-vs-satin-charmeuse-comparison'
                    className='flex h-10 w-1/2 flex-1 items-center justify-center border-1 border-solid border-80583F text-80583F lg_w-auto lg_min-w-[165px] lg_flex-none lg_hover_bg-80583F lg_hover_text-white'
                  >
                    LEARN MORE
                  </Link>
                  <Link
                    href='/aw-satin-charmeuse-swatches-for-bridal-dresses-awsk210u2stcp.html'
                    className='flex h-10 w-1/2 flex-1 items-center justify-center border-1 border-solid border-80583F  bg-80583F uppercase text-white lg_w-auto lg_min-w-[165px] lg_flex-none lg_hover_bg-white lg_hover_text-80583F'
                  >
                    Shop Swatches
                  </Link>
                </div>
              </div>

              <Image
                src={`${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/fabric-2.webp`}
                alt='Crystal Satin'
                width={400}
                height={400}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
